import { convertUtcToTimezone } from '@/shared/lib/datetime';
import { logError } from '@/shared/lib/logging';

import type { Pattern } from '../model/types/pattern';

/**
 * Interface for the temporal distribution data
 */
export interface TemporalDistributionData {
    timeInterval: string;
    counter: number;
    bullishPercentage: number;
    bearishPercentage: number;
    notVerifiedPercentage: number;
    bullishCount: number;
    bearishCount: number;
    notVerifiedCount: number;
}

/**
 * Interface for the temporal interval data structure in the JSON
 */
interface HourlyTemporalIntervalItem {
    time_interval: string;
    pattern_index_count: number;
    above_t_perc: number;
    below_t_perc: number;
    between_t_perc: number;
    price_above_t_post_sum: number;
    price_below_t_post_sum: number;
    price_between_t_post_sum: number;
}

interface DailyTemporalIntervalItem {
    day_of_week: string;
    pattern_index_count: number;
    above_t_perc: number;
    below_t_perc: number;
    between_t_perc: number;
    price_above_t_post_sum: number;
    price_below_t_post_sum: number;
    price_between_t_post_sum: number;
}

/**
 * Gets a property from the pattern and parses JSON if needed
 * @param pattern - The source pattern
 * @param property - The property to get
 * @returns The property value parsed from JSON if it's string, or as is if not string
 */
export const getJsonProperty = <T>(
    pattern: Partial<Pattern>,
    property: keyof Pattern,
): T => {
    const value = pattern[property];

    if (typeof value === 'string') {
        try {
            return JSON.parse(value) as T;
        } catch (error) {
            throw new Error(`Failed to parse JSON for ${property}: ${error}`);
        }
    }

    return value as unknown as T;
};

/**
 * Adapts pattern hourly temporal distribution data
 * @param pattern - The pattern to adapt
 * @param timezoneKey - The timezone key to use for time conversion
 * @returns Array of TemporalDistributionData for hourly intervals
 */
export const adaptPatternToHourlyDistribution = (
    pattern: Partial<Pattern>,
    timezoneKey: string | null,
): TemporalDistributionData[] => {
    if (!pattern.json_temporal_interval_1H) {
        const errorMessage = `Hourly temporal distribution data not available for pattern ${pattern.id}`;

        logError(errorMessage);
        throw new Error(errorMessage);
    }

    const hourlyData = getJsonProperty<HourlyTemporalIntervalItem[]>(
        pattern,
        'json_temporal_interval_1H',
    );

    if (!hourlyData || !Array.isArray(hourlyData)) {
        const errorMessage = `Invalid hourly temporal distribution data for pattern ${pattern.id}`;

        logError(errorMessage);
        throw new Error(errorMessage);
    }

    return hourlyData.map((item) => {
        const {
            time_interval: timeIntervalValue,
            pattern_index_count: counterValue,
            above_t_perc: bullishValue,
            below_t_perc: bearishValue,
            between_t_perc: notVerifiedValue,
        } = item;

        // Convert the time interval if a valid timezone key is provided
        const convertedTimeInterval = timezoneKey
            ? convertUtcToTimezone(timeIntervalValue, timezoneKey)
            : timeIntervalValue;

        return {
            timeInterval: convertedTimeInterval,
            counter: counterValue,
            bullishPercentage: bullishValue,
            bearishPercentage: bearishValue,
            notVerifiedPercentage: notVerifiedValue,
            bullishCount: Math.round((bullishValue * counterValue) / 100),
            bearishCount: Math.round((bearishValue * counterValue) / 100),
            notVerifiedCount: Math.round(
                (notVerifiedValue * counterValue) / 100,
            ),
        };
    });
};

/**
 * Adapts pattern daily temporal distribution data
 * @param pattern - The pattern to adapt
 * @returns Array of TemporalDistributionData for daily intervals
 */
export const adaptPatternToDailyDistribution = (
    pattern: Partial<Pattern>,
): TemporalDistributionData[] => {
    if (!pattern.json_temporal_interval_daily) {
        throw new Error(
            `Daily temporal distribution data not available for pattern ${pattern.id}`,
        );
    }

    const dailyData = getJsonProperty<DailyTemporalIntervalItem[]>(
        pattern,
        'json_temporal_interval_daily',
    );

    if (!dailyData || !Array.isArray(dailyData)) {
        throw new Error(
            `Invalid daily temporal distribution data for pattern ${pattern.id}`,
        );
    }

    return dailyData.map((item) => {
        const {
            day_of_week: timeIntervalValue,
            pattern_index_count: counterValue,
            above_t_perc: bullishValue,
            below_t_perc: bearishValue,
            between_t_perc: notVerifiedValue,
        } = item;

        return {
            timeInterval: timeIntervalValue,
            counter: counterValue,
            bullishPercentage: bullishValue,
            bearishPercentage: bearishValue,
            notVerifiedPercentage: notVerifiedValue,
            bullishCount: Math.round((bullishValue * counterValue) / 100),
            bearishCount: Math.round((bearishValue * counterValue) / 100),
            notVerifiedCount: Math.round(
                (notVerifiedValue * counterValue) / 100,
            ),
        };
    });
};
